import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import { io } from 'socket.io-client';
import { useGlobalContext } from 'hooks';

const SocketListener = ({ children }) => {
  const socketRef = useRef(null);
  const store = useGlobalContext();
  const router = useRouter();

  useEffect(() => {
    // Create socket once on mount
    const socket = io('', {
      path: '/api/v1/realtime',
      transports: ['websocket'],
    });
    socketRef.current = socket;

    const onConnect = () => {
      store.hasHydrated = false;
      socket.emit('join', 'state');
      socket.emit('ping');
    };
    const onPong = () => {};
    const onUpdate = (data, dataHash) => {
      if (dataHash === store.lastReceivedHash || dataHash === store.hash) return;

      store.updating = true;

      runInAction(() => {
        const wasHydrated = store.hasHydrated;
        store.lastReceivedHash = dataHash;
        for (const [k, v] of Object.entries(data)) {
          store[k] = v;
        }
        store.hasHydrated = true;
        if (!wasHydrated) {
          // Avoid rebroadcast if local hash differs from initial payload.
          store.lastReceivedHash = store.hash;
        }
      });

      store.updating = false;
    };
    const onCleared = () => {
      if (router.pathname === '/clear') {
        router.replace('/');
      } else {
        router.reload();
      }
    };

    socket.on('connect', onConnect);
    socket.on('pong', onPong);
    socket.on('update', onUpdate);
    socket.on('cleared', onCleared);

    // 🔴 IMPORTANT: cleanup to prevent listener accumulation
    return () => {
      socket.off('connect', onConnect);
      socket.off('pong', onPong);
      socket.off('update', onUpdate);
      socket.off('cleared', onCleared);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [router, store]);

  // Emit updates when your hash changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    if (!store.hasHydrated) return;
    if (store.updating || store.hash === store.lastReceivedHash) return;

    socket.emit('update', store.stringified, store.hash);
    store.lastReceivedHash = store.hash;
  }, [store.hash, store.updating, store.lastReceivedHash]);

  return children;
};

SocketListener.propTypes = {
  children: PropTypes.node.isRequired,
};

export default observer(SocketListener);
